import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Grid,
  GridColumn as Column,
} from "@progress/kendo-react-grid";
import { useAccountConfig, AGENT_READ_ONLY_MESSAGE } from "./hooks/useAccountConfig";
import { useAdHocScan } from "./hooks/useAdHocScan";
import AgentReadOnlyBanner from "./components/AgentReadOnlyBanner";
import ScoreCell from "./components/ScoreCell";
import { getAttLabel } from "./utils/carrierDisplay";
import { adHocScanSchema } from "./utils/validationSchemas";
import type { AdHocScanResult } from "./types";

type ScanFormData = z.infer<typeof adHocScanSchema>;

const AdHocScan: React.FC = () => {
  const config = useAccountConfig();
  const { scan } = useAdHocScan();
  const canWrite = config.data?.canWrite ?? false;
  const [results, setResults] = useState<AdHocScanResult[]>([]);
  const [hasScanned, setHasScanned] = useState(false);

  const isCanadian = config.data?.isCanadian ?? false;
  const isTrustCallV3 = config.data?.isTrustCallVersion3 ?? false;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ScanFormData>({
    resolver: zodResolver(adHocScanSchema),
    defaultValues: { phoneNumbers: "" },
  });

  const onSubmit = (data: ScanFormData) => {
    setHasScanned(false);
    scan.mutate(data.phoneNumbers, {
      onSuccess: (data) => {
        setResults(data ?? []);
        setHasScanned(true);
      },
      onSettled: () => {
        setHasScanned(true);
      },
    });
  };

  return (
    <div>
      <p className="text-muted mb-3">
        Check phone numbers without adding them to your monitored list.
        Results are not saved.
      </p>

      <AgentReadOnlyBanner />

      {/* Scan Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="mb-4">
        <div className="mb-3">
          <label className="form-label fw-semibold">Phone Numbers</label>
          <textarea
            className={`form-control ${errors.phoneNumbers ? "is-invalid" : ""}`}
            rows={5}
            placeholder={"Enter phone numbers, one per line:\n5551234567\n5559876543\n8005551234"}
            {...register("phoneNumbers")}
          />
          {errors.phoneNumbers && (
            <div className="invalid-feedback">
              {errors.phoneNumbers.message}
            </div>
          )}
          <div className="form-text">
            Enter 10-digit phone numbers, one per line. No formatting needed.
          </div>
        </div>

        <button
          type="submit"
          className="btn btn-primary"
          disabled={scan.isPending || !canWrite}
          title={!canWrite ? AGENT_READ_ONLY_MESSAGE : undefined}
        >
          {scan.isPending ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Scanning...
            </>
          ) : (
            "Scan"
          )}
        </button>
      </form>

      {/* Results Grid */}
      {hasScanned && (
        <div>
          <h6 className="mb-2">
            Results ({results.length} number{results.length !== 1 ? "s" : ""})
          </h6>

          {results.length === 0 ? (
            <div className="alert alert-info">
              No results returned. Check that the phone numbers are valid 10-digit US numbers.
            </div>
          ) : (
            <Grid
              data={results}
              resizable={true}
              reorderable={true}
              style={{ maxHeight: "calc(100vh - 500px)" }}
            >
              <Column field="phoneNumber" title="Phone Number" width={140} />
              <Column
                field="scamScore"
                title="Current Score"
                width={110}
                cell={ScoreCell}
              />
              <Column
                field="historicalScore"
                title="Historical"
                width={100}
                cell={ScoreCell}
              />
              <Column
                field="maxScamScore"
                title="Max Score"
                width={100}
                cell={ScoreCell}
              />

              {isTrustCallV3 && (
                <Column
                  field="verizonScore"
                  title="Verizon"
                  width={90}
                  cell={ScoreCell}
                />
              )}
              {isTrustCallV3 && (
                <Column
                  field="attScore"
                  title={getAttLabel(isCanadian)}
                  width={100}
                  cell={ScoreCell}
                />
              )}
              {isTrustCallV3 && (
                <Column
                  field="tMobileScore"
                  title="T-Mobile"
                  width={90}
                  cell={ScoreCell}
                />
              )}

              <Column field="roboKillerStatus" title="RoboKiller" width={100} cell={ScoreCell} />
              <Column field="nomoroboStatus" title="Nomorobo" width={100} cell={ScoreCell} />
            </Grid>
          )}

          <div className="alert alert-warning mt-3 small">
            These results are temporary and have <strong>not</strong> been added
            to your monitored phone numbers.
          </div>
        </div>
      )}
    </div>
  );
};

export default AdHocScan;
